import { type User, UserRole } from "@employee-management/common";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { toast } from "sonner";
import { authService } from "./auth-service";
import { useQueryClient } from "@tanstack/react-query";

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  logoutAll: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  updateProfile: (name: string) => Promise<boolean>;
  changePassword: (
    currentPassword: string,
    newPassword: string
  ) => Promise<boolean>;
  hasRole: (role: UserRole) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const queryClient = useQueryClient();

  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user;

  // Check if user has required role or higher
  const hasRole = (requiredRole: UserRole): boolean => {
    if (!user) return false;

    const roleHierarchy = {
      [UserRole.SUPERVISOR]: 1,
      [UserRole.MANAGER]: 2,
      [UserRole.SUPERADMIN]: 3,
    };

    const userLevel = roleHierarchy[user.role];
    const requiredLevel = roleHierarchy[requiredRole];

    return userLevel >= requiredLevel;
  };

  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const result = await authService.refreshToken();
      setUser(result.user);
      return true;
    } catch (error) {
      console.error("Token refresh failed:", error);
      setUser(null);
      authService.clearTokens();
      return false;
    }
  }, []);

  // Initialize authentication state
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const tokens = authService.getStoredTokensPublic();
        if (tokens?.accessToken) {
          // Try to get user profile
          const profile = await authService.getProfile();
          setUser(profile);
        }
      } catch (_error) {
        // Clear invalid tokens
        authService.clearTokens();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Set up token refresh interval
  useEffect(() => {
    if (!isAuthenticated) return;

    const refreshInterval = setInterval(
      async () => {
        try {
          await refreshToken();
        } catch (error) {
          console.error("Token refresh failed:", error);
          // Don't logout immediately, let the next API call handle it
        }
      },
      10 * 60 * 1000
    ); // Refresh every 10 minutes

    return () => clearInterval(refreshInterval);
  }, [isAuthenticated, refreshToken]);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      const result = await authService.login(email, password);
      setUser(result.user);
      toast.success("Login successful");
      return true;
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Login failed");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const invalidateQueries = () => {
    queryClient.clear();
  };

  const logout = async (): Promise<void> => {
    try {
      await authService.logout();
      invalidateQueries();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      setUser(null);
      authService.clearTokens();
      toast.success("Logged out successfully");
    }
  };

  const logoutAll = async (): Promise<void> => {
    try {
      await authService.logoutAll();
      invalidateQueries();
    } catch (error) {
      console.error("Logout all error:", error);
    } finally {
      setUser(null);
      authService.clearTokens();
      toast.success("Logged out from all devices");
    }
  };

  const updateProfile = async (name: string): Promise<boolean> => {
    try {
      const updatedUser = await authService.updateProfile(name);
      setUser(updatedUser);
      toast.success("Profile updated successfully");
      return true;
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to update profile"
      );
      return false;
    }
  };

  const changePassword = async (
    currentPassword: string,
    newPassword: string
  ): Promise<boolean> => {
    try {
      await authService.changePassword(currentPassword, newPassword);
      toast.success("Password changed successfully");
      return true;
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to change password"
      );
      return false;
    }
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    logoutAll,
    refreshToken,
    updateProfile,
    changePassword,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
